import { useEffect, useRef, useState } from "react";
import { useT, translateError } from "../i18n/hooks.ts";
import type { Language } from "../model/language.ts";
import type { LoadedProject } from "../model/project.ts";
import type { Theme } from "../model/theme.ts";
import type { EvalHidProject } from "../model/types.ts";
import type { ValidationResult } from "../model/validations.ts";
import {
  updateProjectFromCsvFolder,
  type CsvUpdateSummary,
} from "../model/updateProjectFromCsv.ts";
import type { ExportOutcome } from "./useProjectPersistence.ts";
import { AemetJobsPopover } from "./aemet/AemetJobsPopover.tsx";
import { useActiveAemetJobsCount, useAemetJobsCount } from "./aemet/useAemetJobsCount.ts";
import { CloudDownloadIcon } from "./CloudDownloadIcon.tsx";
import { DownloadIcon } from "./DownloadIcon.tsx";
import { RefreshFromFilesIcon } from "./RefreshFromFilesIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { HelpIcon } from "./HelpIcon.tsx";
import { InfoCircleIcon } from "./InfoCircleIcon.tsx";
import { BellIcon } from "./BellIcon.tsx";
import { ChartIcon } from "./ChartIcon.tsx";
import { ViewIcon } from "./ViewIcon.tsx";
import { PencilIcon } from "./PencilIcon.tsx";
import { RecalculateIcon } from "./RecalculateIcon.tsx";
import { CalibrationIcon } from "./CalibrationIcon.tsx";
import { CloseProjectIcon } from "./CloseProjectIcon.tsx";
import { RenameIcon } from "./RenameIcon.tsx";
import { DuplicateIcon } from "./DuplicateIcon.tsx";
import { MenuIcon } from "./MenuIcon.tsx";
import { NoticesPopover } from "./NoticesPopover.tsx";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.tsx";
import { Dialog } from "./Dialog.tsx";
import { ExportChoiceDialog } from "./ExportChoiceDialog.tsx";
import { UpdateFromCsvDialog } from "./UpdateFromCsvDialog.tsx";
import { RenameDialog } from "./RenameDialog.tsx";
import { PANELS, type PanelKind } from "./editor/panelDefinitions.ts";
import { InfoPopover } from "./InfoPopover.tsx";
import { Logo, Wordmark } from "./Logo.tsx";
import { HelpPanel } from "./HelpPanel.tsx";
import { SettingsMenu } from "./SettingsMenu.tsx";
import { SubBasinSearch } from "./SubBasinSearch.tsx";
import { useClickOutside } from "./useClickOutside.ts";
import { useToolbarOverflow } from "./useToolbarOverflow.ts";

type Popover =
  "help" | "info" | "notices" | "project" | "editor" | "settings" | "aemet" | "search" | null;
type ProjectDialog = "saveAs" | "rename" | "delete" | "export" | null;

export function TopBar({
  loadedProject,
  validation,
  calculationError,
  saveError,
  invalidDataWarning,
  resultsAvailable,
  resultsPanelOpen,
  editorPanelOpen,
  calibrationPanelOpen,
  showCalibrationButton,
  mapView,
  calculationOptionsReady,
  calculationProgress,
  theme,
  language,
  onToggleResults,
  onOpenEditorPanel,
  onToggleCalibrationPanel,
  onToggleMapView,
  onOpenCalculationOptions,
  onRecalculate,
  onToggleTheme,
  onChangeLanguage,
  projectName,
  onCloseProject,
  isEphemeral,
  onSaveAs,
  onRename,
  onExport,
  onExportCsv,
  onUpdateProject,
  onDeleteProject,
  onSelectSubBasin,
}: {
  loadedProject: LoadedProject;
  validation: ValidationResult;
  /** Real engine failure on the last calculation attempt (see the note on `calculationError` in App.tsx) — different from validation warnings/blockers. */
  calculationError: string | null;
  /** The last write to IndexedDB actually failed (see the note on `saveError` in appState.ts) — App.tsx already shows this as its own persistent SaveErrorBanner.tsx; also mixed into the bell here so it's not lost from the notices history once that banner clears. */
  saveError: string | null;
  /** Set on the last SUCCESSFUL calculation if it excluded sub-basins for invalid/empty data in their own series (see the note on `invalidDataWarning` in appState.ts) — shown as a regular notice, not like `calculationError` (that one means the calculation failed outright). */
  invalidDataWarning: string | null;
  resultsAvailable: boolean;
  resultsPanelOpen: boolean;
  editorPanelOpen: boolean;
  calibrationPanelOpen: boolean;
  /** `?calibrator` in the URL (see featureFlags.ts) — an in-progress feature, not shown to every visitor yet. */
  showCalibrationButton: boolean;
  mapView: boolean;
  /** Whether the calculation options dialog has been confirmed at least once this session — enables the direct-recalculate button. */
  calculationOptionsReady: boolean;
  /** Non-null while a calculation is running (see calculateProjectAsync, engine.ts) — replaces the button label with progress and disables both calculate buttons meanwhile. */
  calculationProgress: { done: number; total: number } | null;
  theme: Theme;
  language: Language;
  onToggleResults: () => void;
  /** Opens "Edit project" directly on one of the 5 catalogs (pencil menu, see PANELS in EditBar.tsx) — no need to go through a blank panel first. */
  onOpenEditorPanel: (panel: Exclude<PanelKind, null>) => void;
  onToggleCalibrationPanel: () => void;
  onToggleMapView: () => void;
  /** "Calculate": always opens the options dialog (dates, aquifers). */
  onOpenCalculationOptions: () => void;
  /** Secondary button: recalculates with the last confirmed options, without reopening the dialog. */
  onRecalculate: () => void;
  onToggleTheme: () => void;
  onChangeLanguage: (language: Language) => void;
  /** Name this project was saved with (see storage.ts) — also editable from "Your projects" in Landing.tsx. */
  projectName: string;
  /** Returns to the start screen — the project is already saved (autosave), no need to confirm anything. */
  onCloseProject: () => void;
  /** Comes from an example and hasn't been saved as its own copy yet (see storage.ts) — disables "Delete this project"/"Rename": there's nothing really saved to touch. */
  isEphemeral: boolean;
  /** Creates a copy under another name (its own id) and switches to working on it — the source project isn't touched. */
  onSaveAs: (name: string) => void;
  /** Renames THIS project in place (same id) — unlike onSaveAs, doesn't create a copy. */
  onRename: (name: string) => void;
  /** Downloads the project as an .ehdz file (portable backup) — see exportProject.ts. Returns an outcome (rather than throwing) so this component can show "generating…" and any error inline instead of a silent console.error. */
  onExport: () => Promise<ExportOutcome>;
  /** Downloads the project as the original desktop app's own CSV files, zipped — see exportCsvProject.ts. Same ExportOutcome contract as onExport. */
  onExportCsv: () => Promise<ExportOutcome>;
  /** Applies the result of "Update from CSVs…" (see updateProjectFromCsv.ts) to the open project — same state path any other editor uses (onChangeProject/updateProject in App.tsx), just reached from here instead of "Edit project". */
  onUpdateProject: (project: EvalHidProject) => void;
  /** Deletes this project from IndexedDB and returns to the start screen. */
  onDeleteProject: () => void;
  /** SubBasinSearch.tsx picked a match — switches to map view if needed and pans/highlights it (App.tsx's `selectSubBasinFromSearch`, SubBasinMap.tsx's `highlightedSubBasinCode`). */
  onSelectSubBasin: (subBasinCode: number) => void;
}) {
  const t = useT();
  const [popover, setPopover] = useState<Popover>(null);
  const toggle = (p: Popover) => setPopover((current) => (current === p ? null : p));
  const [csvUpdate, setCsvUpdate] = useState<CsvUpdateSummary | null>(null);
  const [csvUpdateProject, setCsvUpdateProject] = useState<EvalHidProject | null>(null);
  const [csvUpdateError, setCsvUpdateError] = useState<string | null>(null);

  async function handleUpdateFromCsv() {
    if (!("showDirectoryPicker" in window)) {
      setCsvUpdateError(t("app.noFileSystemAccessApiError"));
      return;
    }
    let root: FileSystemDirectoryHandle;
    try {
      root = await window.showDirectoryPicker();
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setCsvUpdateError(translateError(err, t));
      return;
    }
    try {
      const { project, summary } = await updateProjectFromCsvFolder(t, loadedProject.project, root);
      setCsvUpdateProject(project);
      setCsvUpdate(summary);
    } catch (err) {
      setCsvUpdateError(translateError(err, t));
    }
  }
  async function handleExport(kind: "ehdz" | "csv") {
    setExportError(null);
    setExporting(kind);
    const outcome = await (kind === "ehdz" ? onExport() : onExportCsv());
    setExporting(null);
    if (outcome.ok) {
      setDialog(null);
    } else {
      setExportError(outcome.error);
    }
  }
  const [dialog, setDialog] = useState<ProjectDialog>(null);
  // Real gap reported live: moving the actual export work off the main
  // thread (a Worker, see exportProject.ts — a real multi-second freeze at
  // Duero scale otherwise) fixed the freeze itself, but the dialog used to
  // close the INSTANT either button was clicked, before the export (still a
  // real few-hundred-ms-to-seconds operation) had even started — no sign
  // anything was happening at all until the file suddenly appeared, and a
  // failure only ever reached the console, never the user.
  const [exporting, setExporting] = useState<"ehdz" | "csv" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const notices = [
    ...loadedProject.notices,
    ...validation.notices,
    ...(invalidDataWarning ? [invalidDataWarning] : []),
  ];
  const errors = [calculationError, saveError].filter((e): e is string => e !== null);
  const totalNotices = errors.length + validation.blocking.length + notices.length;
  const aemetJobsCount = useAemetJobsCount();
  const activeAemetJobsCount = useActiveAemetJobsCount();

  // Opens the Avisos popover by itself right when a calculation finishes
  // WITH an incidence (a real engine failure, or sub-basins excluded/
  // truncated for invalid data — see calculationError/invalidDataWarning's
  // own comments in appState.ts) — before this, a run that produced "0.00"
  // everywhere with no error dialog read as "nothing happened" unless the
  // user thought to open the bell icon themselves and noticed its small
  // badge. Detects the calculationProgress transition from running->done
  // (a ref, not state, so this effect itself doesn't re-fire on every
  // render) rather than firing on every render where an incidence happens
  // to be present — e.g. reopening the project later shouldn't re-pop this.
  const wasCalculating = useRef(false);
  useEffect(() => {
    const isCalculating = calculationProgress !== null;
    if (wasCalculating.current && !isCalculating && (calculationError || invalidDataWarning)) {
      setPopover("notices");
    }
    wasCalculating.current = isCalculating;
  }, [calculationProgress, calculationError, invalidDataWarning]);

  // Closes whichever popover is open (project/info/notices/editor/settings
  // menus) on a click anywhere outside the bar — e.g. on the map/canvas
  // underneath. A click on a DIFFERENT trigger button is still "inside"
  // the bar, so it's left alone: that button's own onClick already
  // switches `popover` via `toggle`. HelpPanel isn't affected either way —
  // it's a full-screen modal with its own backdrop-click/Escape closing.
  const headerRef = useRef<HTMLElement>(null);
  useClickOutside(headerRef, () => setPopover(null), popover !== null);

  // Rename/Save as/Export/Delete — used to live permanently tucked inside
  // the "..." menu, even with the bar mostly empty. Now they render as
  // their own icons directly (kept visible longest-to-shortest in this
  // order: renaming is the most routine of the four, deleting the rarest)
  // and only the ones that don't fit fall back into that menu — see
  // useToolbarOverflow.ts.
  const projectActions = [
    {
      key: "rename",
      Icon: RenameIcon,
      label: t("topbar.renameProject"),
      onClick: () => setDialog("rename"),
      disabled: isEphemeral,
      disabledTitle: isEphemeral ? t("topbar.exampleUnsavedWarning") : undefined,
    },
    {
      key: "saveAs",
      Icon: DuplicateIcon,
      label: t("topbar.saveCopyAsButton"),
      onClick: () => setDialog("saveAs"),
      disabled: false,
      disabledTitle: undefined,
    },
    {
      key: "export",
      Icon: DownloadIcon,
      label: t("topbar.exportCopy"),
      onClick: () => setDialog("export"),
      disabled: false,
      disabledTitle: undefined,
    },
    {
      key: "updateFromCsv",
      Icon: RefreshFromFilesIcon,
      label: t("topbar.updateFromCsv"),
      onClick: handleUpdateFromCsv,
      disabled: false,
      disabledTitle: undefined,
    },
    {
      key: "delete",
      Icon: TrashIcon,
      label: t("topbar.deleteThisProject"),
      onClick: () => setDialog("delete"),
      disabled: isEphemeral,
      disabledTitle: isEphemeral ? t("topbar.exampleUnsavedWarning") : undefined,
    },
  ];
  const visibleActionCount = useToolbarOverflow(headerRef, projectActions.length);
  const hiddenActions = projectActions.slice(visibleActionCount);

  return (
    <header className="top-bar" ref={headerRef}>
      <div className="brand-bar">
        <Logo size={26} />
        <Wordmark size={17} />
      </div>

      <span className="project-name" title={projectName}>
        {projectName}
      </span>
      <span className="chip">
        {loadedProject.project.subBasins.length} {t("topbar.subBasins")}
      </span>

      <button
        className="button-icon"
        onClick={onCloseProject}
        aria-label={t("topbar.closeProject")}
        title={t("topbar.closeProject")}
      >
        <CloseProjectIcon />
      </button>

      {projectActions.slice(0, visibleActionCount).map((a) => (
        <button
          key={a.key}
          className="button-icon"
          onClick={a.onClick}
          disabled={a.disabled}
          aria-label={a.label}
          title={a.disabledTitle ?? a.label}
        >
          <a.Icon />
        </button>
      ))}

      {hiddenActions.length > 0 && (
        <div style={{ position: "relative" }}>
          <button
            className="button-icon"
            onClick={() => toggle("project")}
            aria-label={t("topbar.moreOptions")}
            title={t("topbar.moreOptions")}
          >
            <MenuIcon />
          </button>
          {popover === "project" && (
            <div className="popover menu-popover">
              {hiddenActions.map((a) => (
                <button
                  key={a.key}
                  className={a.key === "delete" ? "button button-danger" : "button"}
                  onClick={() => {
                    setPopover(null);
                    a.onClick();
                  }}
                  disabled={a.disabled}
                  title={a.disabledTitle}
                >
                  <a.Icon />
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {dialog === "saveAs" && (
        <RenameDialog
          title={t("topbar.saveAsDialogTitle")}
          initialName={t("topbar.copyNameSuffix", { name: projectName })}
          onSave={(name) => {
            onSaveAs(name);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === "rename" && (
        <RenameDialog
          title={t("topbar.renameDialogTitle")}
          initialName={projectName}
          onSave={(name) => {
            onRename(name);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === "delete" && (
        <ConfirmDeleteDialog
          title={t("topbar.deleteDialogTitle")}
          name={projectName}
          message={t("topbar.deleteProjectMessage", { name: projectName })}
          onConfirm={onDeleteProject}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === "export" && (
        <ExportChoiceDialog
          onExportEhdz={() => handleExport("ehdz")}
          onExportCsv={() => handleExport("csv")}
          exporting={exporting}
          error={exportError}
          onClose={() => setDialog(null)}
        />
      )}

      {csvUpdate && csvUpdateProject && (
        <UpdateFromCsvDialog
          summary={csvUpdate}
          onApply={() => {
            onUpdateProject(csvUpdateProject);
            setCsvUpdate(null);
            setCsvUpdateProject(null);
          }}
          onClose={() => {
            setCsvUpdate(null);
            setCsvUpdateProject(null);
          }}
        />
      )}

      {csvUpdateError && (
        <Dialog title={t("topbar.updateFromCsv")} onClose={() => setCsvUpdateError(null)}>
          <p>{csvUpdateError}</p>
          <div className="dialog-actions">
            <button className="button" onClick={() => setCsvUpdateError(null)}>
              {t("common.close")}
            </button>
          </div>
        </Dialog>
      )}

      <div className="bar-separator" />

      <button
        className="button-icon"
        onClick={() => toggle("help")}
        aria-label={t("topbar.help")}
        title={t("topbar.help")}
      >
        <HelpIcon />
      </button>
      {popover === "help" && <HelpPanel onClose={() => setPopover(null)} />}

      <div style={{ position: "relative" }}>
        <button
          className="button-icon"
          onClick={() => toggle("info")}
          aria-label={t("topbar.projectInfo")}
        >
          <InfoCircleIcon />
        </button>
        {popover === "info" && <InfoPopover loadedProject={loadedProject} />}
      </div>

      <div style={{ position: "relative" }}>
        <button
          className="button-icon"
          onClick={() => toggle("notices")}
          aria-label={t("topbar.notices")}
        >
          <BellIcon />
          {totalNotices > 0 && <span className="badge">{totalNotices}</span>}
        </button>
        {popover === "notices" && (
          <NoticesPopover errors={errors} blocking={validation.blocking} notices={notices} />
        )}
      </div>

      {aemetJobsCount > 0 && (
        <div style={{ position: "relative" }}>
          <button
            className="button-icon"
            onClick={() => toggle("aemet")}
            aria-label={t("topbar.aemetDownloads")}
            title={t("topbar.aemetDownloads")}
          >
            <CloudDownloadIcon />
            {activeAemetJobsCount > 0 && <span className="badge">{activeAemetJobsCount}</span>}
          </button>
          {popover === "aemet" && <AemetJobsPopover />}
        </div>
      )}

      <SubBasinSearch
        subBasins={loadedProject.project.subBasins}
        open={popover === "search"}
        onToggle={() => toggle("search")}
        onClose={() => setPopover(null)}
        onSelect={onSelectSubBasin}
      />

      <button
        className="button-icon"
        onClick={onToggleMapView}
        aria-label={t("topbar.changeView")}
        title={mapView ? t("topbar.viewAsCards") : t("topbar.viewOnMap")}
      >
        <ViewIcon />
        {!mapView && <span className="badge" />}
      </button>

      <div style={{ position: "relative" }}>
        <button
          className="button-icon"
          onClick={() => toggle("editor")}
          aria-label={t("topbar.editProject")}
          title={t("topbar.editProject")}
        >
          <PencilIcon />
          {editorPanelOpen && <span className="badge" />}
        </button>
        {popover === "editor" && (
          <div className="popover menu-popover">
            {PANELS.map((p) => (
              <button
                key={p.id}
                className="button"
                onClick={() => {
                  setPopover(null);
                  onOpenEditorPanel(p.id);
                }}
              >
                <p.Icon /> {t(p.label)}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCalibrationButton && (
        <button
          className="button-icon"
          onClick={onToggleCalibrationPanel}
          aria-label={t("topbar.calibration")}
          title={t("topbar.calibration")}
        >
          <CalibrationIcon />
          {calibrationPanelOpen && <span className="badge" />}
        </button>
      )}

      <div className="calculate-group">
        <button
          className="button"
          onClick={onOpenCalculationOptions}
          disabled={validation.blocking.length > 0 || calculationProgress !== null}
          title={
            validation.blocking.length > 0
              ? t("topbar.cannotCalculate", { reasons: validation.blocking.join(" ") })
              : t("topbar.calculateTitle")
          }
        >
          {calculationProgress
            ? t("topbar.calculating", {
                n: calculationProgress.done,
                total: calculationProgress.total,
              })
            : t("topbar.calculate")}
        </button>
        {calculationOptionsReady && (
          <button
            className="button-icon"
            onClick={onRecalculate}
            disabled={validation.blocking.length > 0 || calculationProgress !== null}
            aria-label={t("topbar.recalculate")}
            title={t("topbar.recalculateTitle")}
          >
            <RecalculateIcon />
          </button>
        )}
      </div>

      <button
        className="button-icon"
        onClick={onToggleResults}
        disabled={!resultsAvailable}
        aria-label={t("topbar.results")}
        title={t("topbar.results")}
      >
        <ChartIcon />
        {resultsPanelOpen && resultsAvailable && <span className="badge" />}
      </button>

      <SettingsMenu
        theme={theme}
        onToggleTheme={onToggleTheme}
        language={language}
        onChangeLanguage={onChangeLanguage}
        open={popover === "settings"}
        onToggle={() => toggle("settings")}
        onClose={() => setPopover(null)}
      />
    </header>
  );
}
